<script setup lang="ts">
import { type Component, computed } from 'vue'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'

interface AvatarInitialsProps {
  name: string
  size: 'big' | 'huge' | 'medium' | 'small' | 'tiny'
  fallbackIcon?: Component | undefined
  image?: null | string | undefined
  isResponsibleEmployee?: boolean | undefined
}

const props = defineProps<AvatarInitialsProps>()

const backgroundColors = [
  'bg-broom-700',
  'bg-broom-950',
  'bg-keppel-600',
  'bg-lightning_yellow-600',
  'bg-lightning_yellow-800',
  'bg-lochmara-600',
  'bg-lochmara-900',
  'bg-radical_red-800',
  'bg-seagull-600',
  'bg-seagull-800',
]

const initialsMaxLength = 2

const initials = computed(() => {
  return props.name
    .split(' ')
    .map((word) => {
      return word[0]
    })
    .join('')
    .slice(0, initialsMaxLength)
    .toUpperCase()
})

const colorIndex = computed(() => {
  const charCodeSum = props.name
    .split('')
    .map((char) => {
      return char.charCodeAt(0)
    })
    .reduce((acc, cur) => {
      return acc + cur
    }, 0)

  return charCodeSum % backgroundColors.length
})

const componentSize = {
  big: 'h-12 w-12',
  huge: 'h-28 w-28',
  medium: 'h-8 w-8',
  small: 'h-6 w-6',
  tiny: 'h-4 w-4',
}

const fontSize = {
  big: 'text-lg',
  huge: 'text-3xl',
  medium: 'text-sm',
  small: 'text-xs',
  tiny: 'text-[0.625rem]',
}
</script>

<template>
  <div
    class="flex shrink-0 items-center justify-center gap-2.5 overflow-hidden rounded-full"
    :class="[
      componentSize[props.size],
      props.isResponsibleEmployee ? 'bg-lynch-500' : `${backgroundColors[colorIndex]}`,
    ]"
  >
    <div
      v-if="!props.image && !props.fallbackIcon"
      class="select-none text-white"
      :class="fontSize[props.size]"
      :data-testid="TEST_IDS_CREATE.avatar.initials"
    >
      {{ initials }}
    </div>
    <img
      v-else-if="props.image"
      :alt="props.name"
      class="h-full w-full rounded-full object-cover"
      :src="props.image"
    />
    <div v-else-if="props.fallbackIcon" class="grid h-full w-full place-items-center bg-black-100">
      <component :is="props.fallbackIcon" class="h-20 w-20 text-black-400" />
    </div>
  </div>
</template>
